import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { toast } from "sonner";
import { Award, ExternalLink, Linkedin, Share2, GraduationCap, ArrowRight, CheckCircle2 } from "lucide-react";

function frenchDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function PublicProfile() {
  const { code } = useParams();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/public/profile/${code}`)
      .then(r => setProfile(r.data))
      .catch(() => setNotFound(true));
  }, [code]);

  const shareUrl = `${window.location.origin}/profil/${code}`;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Lien du profil copié !"); };

  if (notFound) return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="public-profile-not-found">
      <div className="w-16 h-16 rounded-full bg-ink-50 border border-ink-200 mx-auto flex items-center justify-center mb-4">
        <GraduationCap className="w-8 h-8 text-ink-400" />
      </div>
      <h1 className="font-display text-2xl font-bold text-ink-900">Profil introuvable</h1>
      <p className="text-ink-600 mt-2 text-sm">
        Ce profil n'existe pas ou n'a pas été rendu public par son propriétaire.
      </p>
      <Link to="/" className="inline-block mt-6 px-5 py-2.5 rounded-lg bg-gold-500 text-white font-medium hover:bg-gold-600">
        Retour à l'accueil
      </Link>
    </div>
  );

  if (!profile) return <div className="p-10 text-center text-ink-500 font-mono text-sm">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="public-profile-page">
      {/* Hero */}
      <section className="surface rounded-3xl p-8 md:p-10 shadow-pop">
        <div className="grid md:grid-cols-[1fr,auto] gap-6 items-start">
          <div>
            <div className="text-xs uppercase tracking-widest text-gold-700 font-mono font-semibold mb-3">Profil certifié E.C.S</div>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-ink-900 tracking-tight" data-testid="profile-name">{profile.full_name}</h1>
            {profile.title && (
              <div className="text-ink-700 mt-2 text-lg" data-testid="profile-title">{profile.title}</div>
            )}
            {profile.bio && (
              <p className="mt-4 text-ink-600 leading-relaxed max-w-2xl whitespace-pre-line" data-testid="profile-bio">{profile.bio}</p>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {profile.linkedin_url && (
                <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-200 hover:bg-ink-50 text-sm text-ink-800 font-medium"
                   data-testid="profile-linkedin">
                  <Linkedin className="w-4 h-4" /> LinkedIn <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gold-300 text-gold-700 hover:bg-gold-50 text-sm font-medium" data-testid="profile-share-button">
                <Share2 className="w-4 h-4" /> Partager le profil
              </button>
              <span className="text-xs text-ink-500">Membre depuis {new Date(profile.member_since).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
            </div>
          </div>

          <div className="hidden md:flex flex-col items-center">
            <div className="bg-white p-2 border border-ink-200 rounded-lg">
              <QRCodeSVG value={shareUrl} size={100} bgColor="#FFFFFF" fgColor="#0F172A" />
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-500 mt-2">QR profil</div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-ink-100 grid grid-cols-3 gap-4">
          <div>
            <div className="font-display text-2xl font-bold text-ink-900" data-testid="profile-cert-count">{profile.certificates.length}</div>
            <div className="text-[11px] uppercase tracking-widest text-ink-500 font-medium">certifications</div>
          </div>
          <div>
            <div className="font-display text-2xl font-bold text-ink-900">
              {new Set(profile.certificates.map(c => c.category_name).filter(Boolean)).size}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-ink-500 font-medium">domaines</div>
          </div>
          <div>
            <div className="font-display text-2xl font-bold text-gold-gradient font-mono">{profile.referral_code}</div>
            <div className="text-[11px] uppercase tracking-widest text-ink-500 font-medium">code</div>
          </div>
        </div>
      </section>

      {/* Certificates */}
      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold text-ink-900 mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-gold-600" /> Certifications validées
        </h2>
        {profile.certificates.length === 0 ? (
          <div className="surface rounded-2xl p-10 text-center text-ink-500 text-sm">
            Aucun certificat public pour le moment.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {profile.certificates.map(c => (
              <Link key={c.unique_code} to={`/verify/${c.unique_code}`}
                className="surface rounded-2xl p-5 hover:shadow-pop transition group"
                data-testid={`profile-cert-${c.unique_code}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gold-50 border border-gold-200 flex items-center justify-center">
                    <Award className="w-5 h-5 text-gold-700" />
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-success bg-success/10 border border-success/30 px-2 py-1 rounded-full font-mono">
                    <CheckCircle2 className="w-3 h-3" /> Vérifié
                  </span>
                </div>
                <h3 className="font-display text-lg font-semibold text-ink-900 group-hover:text-gold-700 transition-colors">{c.certification_title}</h3>
                <div className="text-xs text-ink-500 mt-1">{c.category_name}</div>
                <div className="mt-4 pt-3 border-t border-ink-100 flex items-center justify-between text-xs text-ink-500">
                  <span>{frenchDate(c.issued_at)}</span>
                  <span className="font-mono">{c.unique_code}</span>
                </div>
                <div className="mt-2 flex items-center justify-end text-xs text-gold-700 group-hover:gap-2 transition-all inline-flex gap-1">
                  Vérifier <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="mt-14 text-center">
        <p className="text-sm text-ink-500 mb-3">Vous aussi, mettez vos compétences en avant.</p>
        <Link to="/register" className="px-6 py-3 rounded-lg bg-gold-500 text-white font-semibold hover:bg-gold-600 shadow-gold inline-flex items-center gap-2">
          Créer mon profil E.C.S <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
